/**
 * The one figure a surface is about.
 *
 * It takes an already-formatted string, never a number: formatting a money
 * amount is `CresultcardMoney`'s job and formatting a count is
 * `cresultcard.format`'s, and a component that accepts both a number and a
 * currency ends up owning a second, quieter money contract.
 *
 * `scale` is a step on a closed five-step ramp, never a pixel size. This is
 * the only component in the kit permitted above the title step — which is what
 * makes "the one figure" true rather than aspirational.
 */
"use client";
import type { ReactElement, ReactNode } from "react";
import {
  CRC_FIGURE_SIZE,
  CRC_NUMERIC_STYLE,
  CRC_TEXT_FAINT,
  CRC_TEXT_MUTED,
  CRC_TONE_COLOR,
  CRC_TYPE_STYLE,
} from "./cresultcard.tokens";
import {
  CresultcardAlign,
  CresultcardFigureScale,
  CresultcardTextStep,
  CresultcardTone,
} from "./cresultcard.types";

/** The unit sits on the figure's baseline at a fixed fraction of its size. */
const UNIT_SIZE_RATIO = 0.42;
const UNIT_MIN_SIZE = 9;

interface CresultcardFocalProps {
  /** Already formatted. The figure renders it verbatim. */
  readonly value: string;
  readonly unit?: string;
  /**
   * What the figure IS, above it — a unit gloss, a measure name. Without it the
   * only place to put one was a wrapper span the caller had to build itself,
   * which put the gloss outside the figure's own alignment.
   */
  readonly title?: ReactNode;
  readonly tone?: CresultcardTone;
  readonly scale: CresultcardFigureScale;
  /**
   * Which edge the block sits against. `End` is the headline's figure column,
   * which is why it is the default; a figure STRETCHED in a column stack was
   * right-aligned against a left-read column with no way to say otherwise.
   */
  readonly align?: CresultcardAlign;
  /** One supporting line beneath the figure — a scope, a baseline, an as-of. */
  readonly support?: ReactNode;
}

export function CresultcardFocal({
  value,
  unit,
  title,
  tone = CresultcardTone.Neutral,
  scale,
  align = CresultcardAlign.End,
  support,
}: CresultcardFocalProps): ReactElement {
  const size = CRC_FIGURE_SIZE[scale];
  const alignEnd = align === CresultcardAlign.End;

  return (
    <span
      data-el="Cresultcard/Focal/Root"
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: alignEnd ? "flex-end" : "flex-start",
        gap: 3,
        minWidth: 0,
      }}
    >
      {title != null && (
        <span
          style={{
            ...CRC_TYPE_STYLE[CresultcardTextStep.Eyebrow],
            fontSize: 8.5,
            textAlign: alignEnd ? "end" : "start",
            color: CRC_TEXT_MUTED,
          }}
        >
          {title}
        </span>
      )}
      <span
        style={{
          display: "inline-flex",
          alignItems: "baseline",
          gap: 4,
          minWidth: 0,
        }}
      >
        <span
          style={{
            ...CRC_NUMERIC_STYLE,
            fontSize: size,
            fontWeight: 800,
            letterSpacing: "-0.035em",
            lineHeight: 1,
            whiteSpace: "nowrap",
            color: CRC_TONE_COLOR[tone],
          }}
        >
          {value}
        </span>
        {unit != null && (
          <span
            style={{
              fontSize: Math.max(UNIT_MIN_SIZE, Math.round(size * UNIT_SIZE_RATIO)),
              fontWeight: 560,
              lineHeight: 1,
              whiteSpace: "nowrap",
              color: CRC_TEXT_FAINT,
            }}
          >
            {unit}
          </span>
        )}
      </span>
      {support}
    </span>
  );
}
